import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { selectAccessToken } from '@client/redux/user';
import { selectUserPermissions } from '@client/redux/user/userReducer';
import { Auth0Context } from '@client/helperComponents/react-auth0-spa';
import { checkAuthorized, checkLoggedIn } from './restrictUtils';

export const withRestrictions = ({
  permissions = [],
  shouldBeLoggedIn = false,
}) => {
  const checkRestrictions = ({ userPermissions, isAuthenticated }) => {
    const passedLoggedInCheck = checkLoggedIn(
      shouldBeLoggedIn,
      isAuthenticated
    );
    const passedAuthorizedCheck = checkAuthorized(permissions, userPermissions);

    return {
      passedLoggedInCheck,
      passedAuthorizedCheck,
      passedAllChecks: passedLoggedInCheck && passedAuthorizedCheck,
    };
  };

  return (WrappedComponent, FallbackComponent) => {
    class Restrict extends PureComponent {
      static contextType = Auth0Context;

      static Placeholder = () => (
        <div data-restrict-placeholder="1" hidden aria-hidden="true" />
      );

      renderFallback(props) {
        return FallbackComponent ? <FallbackComponent {...props} /> : null;
      }

      static loadData(store) {
        if (
          WrappedComponent &&
          typeof WrappedComponent.loadData === 'function'
        ) {
          WrappedComponent.loadData(store);
        }
      }

      render() {
        const { userPermissions, accessToken, ...otherProps } = this.props;
        const { passedLoggedInCheck, passedAllChecks } = checkRestrictions({
          userPermissions,
          isAuthenticated: !!accessToken,
        });

        if (!accessToken && this.context && this.context.loading) {
          return <Restrict.Placeholder />;
        }

        if (!passedLoggedInCheck) {
          if (process.env.SERVER) {
            // Render a stable placeholder on the server so SSR and client markup match
            return <Restrict.Placeholder />;
          }
          return <Navigate to="/login" replace />;
        }

        return passedAllChecks ? (
          <WrappedComponent {...otherProps} />
        ) : (
          this.renderFallback({ ...otherProps })
        );
      }
    }

    // Expose preload to allow client to preload lazy chunks before hydration
    // This prevents Suspense fallbacks (e.g., Loader divs) from rendering on the client
    // when the server already rendered the full route content.
    Restrict.preload = () => {
      const tasks = [];
      try {
        if (
          WrappedComponent &&
          typeof WrappedComponent.preload === 'function'
        ) {
          tasks.push(WrappedComponent.preload());
        }
      } catch (_) {}
      try {
        if (
          FallbackComponent &&
          typeof FallbackComponent.preload === 'function'
        ) {
          tasks.push(FallbackComponent.preload());
        }
      } catch (_) {}
      return Promise.all(tasks);
    };

    function mapStateToProps(state) {
      return {
        accessToken: selectAccessToken(state),
        userPermissions: selectUserPermissions(state),
      };
    }

    const ConnectedRestrict = connect(mapStateToProps)(Restrict);
    // Preserve the preload static on the connected component
    ConnectedRestrict.preload = Restrict.preload;

    return ConnectedRestrict;
  };
};

export default (restrictions) => (WrapperComponent, FallbackComponent) => {
  return withRestrictions(restrictions)(WrapperComponent, FallbackComponent);
};
